/**
 * scheduleToZh — Translate AWS EventBridge schedule expressions to readable Chinese.
 *
 * AWS EventBridge supports three schedule types:
 *   1. cron(minutes hours day-of-month month day-of-week year) — 6-field AWS cron
 *   2. at(yyyy-MM-ddTHH:mm:ss)                                — one-time (UTC)
 *   3. rate(value unit)                                        — fixed interval
 *
 * AWS cron special characters (all supported):
 *   *    — all values
 *   ?    — no specific value (DOM or DOW, exactly one must be ?)
 *   -    — range (e.g. MON-FRI, 1-15)
 *   ,    — list (e.g. 1,15, MON,WED,FRI)
 *   /    — increment (e.g. 0/15 = every 15 starting at 0)
 *   L    — last (last day of month, or e.g. FRIL = last Friday)
 *   W    — nearest weekday (e.g. 15W = nearest weekday to 15th)
 *   #    — nth weekday (e.g. MON#2 = second Monday)
 *
 * AWS DOW: SUN=1 .. SAT=7 (or 3-letter names)
 */

import {
  computeDayShift,
  convertHourStr,
  cronItems,
  DOW_NAMES,
  isSingleValue,
  isWild,
  minuteListItems,
} from './cronShared';

// ── Constants ───────────────────────────────────────────────────────────────

const DOW_MAP: Record<string, string> = {
  SUN: '周日', MON: '周一', TUE: '周二', WED: '周三',
  THU: '周四', FRI: '周五', SAT: '周六',
  '1': '周日', '2': '周一', '3': '周二', '4': '周三',
  '5': '周四', '6': '周五', '7': '周六',
};

const MONTH_MAP: Record<string, string> = {
  JAN: '1月', FEB: '2月', MAR: '3月', APR: '4月',
  MAY: '5月', JUN: '6月', JUL: '7月', AUG: '8月',
  SEP: '9月', OCT: '10月', NOV: '11月', DEC: '12月',
};

const NTH_ZH = ['', '第一个', '第二个', '第三个', '第四个', '第五个'];

// ── Main entry ──────────────────────────────────────────────────────────────

export function scheduleToZh(expr: string): string {
  if (!expr) return '-';
  try {
    // at() one-time
    const atMatch = expr.match(/^at\((.+)\)$/);
    if (atMatch) return translateAt(atMatch[1]);

    // rate()
    const rateMatch = expr.match(/^rate\((\d+)\s+(minute|hour|day)s?\)$/i);
    if (rateMatch) return translateRate(parseInt(rateMatch[1], 10), rateMatch[2].toLowerCase());

    // cron()
    const cronMatch = expr.match(/^cron\((.+)\)$/);
    if (cronMatch) return translateCron(cronMatch[1].trim());

    return expr;
  } catch {
    return expr;
  }
}

// ── at() ────────────────────────────────────────────────────────────────────

function translateAt(timeStr: string): string {
  const d = new Date(timeStr + 'Z');
  if (isNaN(d.getTime())) return `at(${timeStr})`;
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `一次性 ${m}月${day}日 ${hh}:${mm}`;
}

// ── rate() ──────────────────────────────────────────────────────────────────

function translateRate(n: number, unit: string): string {
  const unitZh = unit === 'minute' ? '分钟' : unit === 'hour' ? '小时' : '天';
  return n === 1 ? `每${unitZh}` : `每 ${n} ${unitZh}`;
}

// ── cron() ──────────────────────────────────────────────────────────────────

function translateCron(cronBody: string): string {
  const parts = cronBody.split(/\s+/);
  if (parts.length !== 6) return `cron(${cronBody})`;

  const [minute, hour, dom, month, dow, year] = parts;

  // Compute the day shift caused by UTC→UTC+8 conversion.
  // If every possible hour value in the field crosses midnight when +8'd, dayShift=1.
  // For wildcard/interval hours we cannot determine a single shift → 0 (no shift).
  const dayShift = computeDayShift(hour);

  const timePart = describeTime(minute, hour);
  const datePart = describeDate(dom, month, dow, dayShift);
  const yearPart = describeYear(year);

  const pieces = [datePart, timePart, yearPart].filter(Boolean);
  return pieces.join(' ') || `cron(${cronBody})`;
}

// ── Time description (minute + hour) ────────────────────────────────────────

function describeTime(minute: string, hour: string): string {
  const isMinWild = isWild(minute);
  const isHourWild = isWild(hour);

  if (isHourWild && isMinWild) return '每分钟';
  if (isHourWild) return describeMinuteOfEveryHour(minute);

  // One hour step at a fixed minute: "从 08:00 起每 2 小时", "16:00 至 01:00 每 2 小时"
  const hours = cronItems(hour);
  if (isSingleValue(minute) && hours.length === 1 && hours[0].step) {
    const { start, end, step } = hours[0];
    const mm = minute.padStart(2, '0');
    const from = `${convertHourStr(start)}:${mm}`;
    return end ? `${from} 至 ${convertHourStr(end)}:${mm} 每 ${step} 小时` : `从 ${from} 起每 ${step} 小时`;
  }

  // One hour with several minutes: "17:00、17:30", "17:00 至 17:30 每 5 分钟"
  const minutes = cronItems(minute);
  if (isSingleValue(hour) && !isMinWild) {
    const at = (m: string) => `${convertHourStr(hour)}:${(m === '*' ? '0' : m).padStart(2, '0')}`;
    if (minutes.length > 1 && minutes.every((m) => isSingleValue(m.start) && !m.end && !m.step)) {
      return minutes.map((m) => at(m.start)).join('、');
    }
    if (minutes.length === 1 && minutes[0].step && minutes[0].end) {
      return `${at(minutes[0].start)} 至 ${at(minutes[0].end)} 每 ${minutes[0].step} 分钟`;
    }
  }

  // Hour has a value; "17时的05分、10至20分" needs the 时 a bare hour leaves out
  const minDesc = describeMinuteForHour(minute);
  const hourDesc = describeHourField(hour);
  const hourUnit = isSingleValue(hour) && minDesc.startsWith('的') ? '时' : '';

  return `${hourDesc}${hourUnit}${minDesc}`;
}

function describeMinuteOfEveryHour(minute: string): string {
  const items = cronItems(minute);
  if (items.length === 1 && items[0].step) {
    const { start, end, step } = items[0];
    if (end) return `每小时第 ${start} 至 ${end} 分钟每 ${step} 分钟`;
    return start === '0' || start === '*' ? `每 ${step} 分钟` : `从第 ${start} 分钟起每 ${step} 分钟`;
  }
  const minutes = minuteListItems(minute).map(({ start, end }) => (end ? `${start}至${end}` : start));
  return `每小时第 ${minutes.join('、')} 分`;
}

// ── Hour field ──────────────────────────────────────────────────────────────

function describeHourField(hour: string): string {
  const items = cronItems(hour);
  if (items.length === 1 && !items[0].end && !items[0].step) return convertHourStr(hour);
  return items
    .map(({ start, end, step }) => {
      if (step) {
        return end
          ? `${convertHourStr(start)}时至${convertHourStr(end)}时每 ${step} 小时`
          : `从 ${convertHourStr(start)} 时起每 ${step} 小时`;
      }
      return end ? `${convertHourStr(start)}时至${convertHourStr(end)}时` : `${convertHourStr(start)}时`;
    })
    .join('、');
}

function describeMinuteForHour(minute: string): string {
  if (isWild(minute)) return '每分钟';
  if (isSingleValue(minute)) return `:${minute.padStart(2, '0')}`;
  const items = cronItems(minute);
  const mm = (m: string) => (m === '*' ? '0' : m).padStart(2, '0');
  if (items.length === 1 && items[0].step) {
    const { start, end, step } = items[0];
    return end ? `:${mm(start)}至:${mm(end)}每${step}分钟` : `:${mm(start)}起每${step}分钟`;
  }
  const minutes = minuteListItems(minute).map(({ start, end }) =>
    end ? `${mm(start)}至${mm(end)}分` : `${mm(start)}分`,
  );
  return '的' + minutes.join('、');
}

// ── Date description (dom + month + dow) ────────────────────────────────────

function describeDate(dom: string, month: string, dow: string, dayShift: number): string {
  const monthPart = describeMonth(month);
  const domPart = describeDom(dom);
  const dowPart = describeDow(dow, dayShift);

  const hasDom = !isWild(dom);
  const hasDow = !isWild(dow);
  const hasMonth = !isWild(month);

  // Every day
  if (!hasDom && !hasDow && !hasMonth) return '每天';

  // Specific weekday(s) only
  // "每周一至周五"; a list starting with a step already reads "从周一起每 2 天"
  if (!hasDom && hasDow && !hasMonth) return cronItems(dow)[0].step ? dowPart : `每${dowPart}`;

  // Specific weekday(s) in specific month(s)
  if (!hasDom && hasDow && hasMonth) return `${monthPart}${dowPart}`;

  // Specific day(s) of month only
  if (hasDom && !hasDow && !hasMonth) return `每月${domPart}`;

  // Specific month + day(s)
  if (hasDom && !hasDow && hasMonth) return `${monthPart}${domPart}`;

  // Both DOM and DOW specified (AWS allows this)
  if (hasDom && hasDow) {
    const parts = [];
    if (hasMonth) parts.push(monthPart);
    parts.push(`${domPart}且${dowPart}`);
    return parts.join('');
  }

  return [monthPart, domPart, dowPart].filter(Boolean).join('');
}

// ── Month ───────────────────────────────────────────────────────────────────

function describeMonth(month: string): string {
  if (isWild(month)) return '';
  return cronItems(month)
    .map(({ start, end, step }) => {
      const range = end ? `${parseSingleMonth(start)}至${parseSingleMonth(end)}` : parseSingleMonth(start);
      if (!step) return range;
      return end ? `${range}每 ${step} 个月` : `从${range}起每 ${step} 个月`;
    })
    .join('、');
}

function parseSingleMonth(m: string): string {
  const upper = m.toUpperCase();
  if (MONTH_MAP[upper]) return MONTH_MAP[upper];
  const n = parseInt(m, 10);
  if (n >= 1 && n <= 12) return `${n}月`;
  return `${m}月`;
}

// ── Day of month ────────────────────────────────────────────────────────────

function describeDom(dom: string): string {
  if (isWild(dom)) return '';

  // L — last day of month
  if (dom === 'L') return '最后一天';

  // LW — last weekday of month
  if (dom.toUpperCase() === 'LW') return '最后一个工作日';

  // W — nearest weekday, e.g. 15W
  const wMatch = dom.match(/^(\d+)W$/i);
  if (wMatch) return `${wMatch[1]}日最近的工作日`;

  // Lists, ranges and steps, e.g. "1,15", "1-7", "1/7", "1,3-5"
  return cronItems(dom)
    .map(({ start, end, step }) => {
      const range = end ? `${start}日至${end}日` : describeSingleDom(start);
      if (!step) return range;
      return end ? `${range}每 ${step} 天` : `从${range}起每 ${step} 天`;
    })
    .join('、');
}

function describeSingleDom(d: string): string {
  if (d === 'L') return '最后一天';
  const wMatch = d.match(/^(\d+)W$/i);
  if (wMatch) return `${wMatch[1]}日最近的工作日`;
  return `${d}日`;
}

// ── Day of week ─────────────────────────────────────────────────────────────

function describeDow(dow: string, dayShift: number): string {
  if (isWild(dow)) return '';

  // L suffix — last X of month, e.g. FRIL or 6L
  // Cannot reliably shift: "last Friday + 1 day" ≠ "last Saturday"
  const lMatch = dow.match(/^(\w+)L$/i);
  if (lMatch) {
    const dayName = parseSingleDow(lMatch[1]);
    return dayShift > 0
      ? `最后一个${dayName}(次日)`
      : `最后一个${dayName}`;
  }

  // # — nth weekday, e.g. MON#2 or 2#3
  // Cannot reliably shift: "2nd Monday + 1 day" ≠ "2nd Tuesday"
  const hashMatch = dow.match(/^(\w+)#(\d)$/);
  if (hashMatch) {
    const dayName = parseSingleDow(hashMatch[1]);
    const nth = parseInt(hashMatch[2], 10);
    const label = `${NTH_ZH[nth] || `第${nth}个`}${dayName}`;
    return dayShift > 0 ? `${label}(次日)` : label;
  }

  // Lists, ranges and steps, e.g. "MON,WED", "MON-FRI", "2/2", "MON-WED,FRI"
  return cronItems(dow)
    .map(({ start, end, step }) => {
      const first = shiftSingleDow(start, dayShift);
      const range = end ? `${first}至${shiftSingleDow(end, dayShift)}` : first;
      if (!step) return range;
      return end ? `${range}每 ${step} 天` : `从${range}起每 ${step} 天`;
    })
    .join('、');
}

function parseSingleDow(d: string): string {
  const upper = d.toUpperCase();
  return DOW_MAP[upper] ?? d;
}

/**
 * Shift a single DOW token forward by `shift` days (0 or 1) and return Chinese name.
 * Handles both 3-letter names (MON) and AWS numeric (2=MON).
 */
function shiftSingleDow(d: string, shift: number): string {
  if (shift === 0) return parseSingleDow(d);

  const upper = d.toUpperCase();
  let idx: number;

  // Try name lookup first
  const nameIdx = DOW_NAMES.indexOf(upper);
  if (nameIdx >= 0) {
    idx = nameIdx;
  } else {
    // AWS numeric: 1=SUN .. 7=SAT
    const num = parseInt(d, 10);
    if (num >= 1 && num <= 7) {
      idx = num - 1; // convert to 0-based
    } else {
      return parseSingleDow(d); // unrecognized, return as-is
    }
  }

  const shifted = (idx + shift) % 7;
  return DOW_MAP[DOW_NAMES[shifted]] ?? d;
}

// ── Year ────────────────────────────────────────────────────────────────────

function describeYear(year: string): string {
  if (isWild(year)) return '';
  const items = cronItems(year);
  // "(2024、2026年)"; with a step every item carries 年: "(2030年、从2020年起每2年)"
  const hasStep = items.some((i) => i.step);
  const text = items
    .map(({ start, end, step }) => {
      const range = end ? `${start}至${end}` : start;
      if (step) return `从${range}年起每${step}年`;
      return hasStep ? `${range}年` : range;
    })
    .join('、');
  return `(${hasStep ? text : `${text}年`})`;
}
